import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Spinner,
  EmptyState,
  EmptyStateGraphic,
  EmptyStateTitle,
  EmptyStateDescription,
} from '@customerio/pluma-components/react';
import { useAuth } from '@/services/auth';
import { accountVerificationApi } from './api';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { VerificationStatusApproved, VerificationStatusEscalated, VerificationStatusRunning } from './components';

export function AccountVerificationStatusPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const auth = useAuth();
  const workspaceId = getWorkspaceIdFromCurrentUrl();

  const {
    data: status,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['accountVerificationStatus', auth.account?.id, jobId],
    queryFn: async () => {
      if (auth.account?.id == null || jobId == null) {
        throw new Error('Missing account ID or job ID');
      }
      const response = await accountVerificationApi.getStatus({
        accountId: String(auth.account.id),
        jobId,
      });
      return response;
    },
    enabled: auth.account?.id != null && jobId != null,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.status === 'running') {
        return 2000;
      }
      return false;
    },
  });

  if (jobId == null) {
    return (
      <Box p="400">
        <EmptyState>
          <EmptyStateTitle icon="error">Invalid verification request</EmptyStateTitle>
          <EmptyStateDescription>Please try submitting again.</EmptyStateDescription>
        </EmptyState>
      </Box>
    );
  }

  if (isLoading && status == null) {
    return (
      <Box p="400">
        <EmptyState>
          <EmptyStateGraphic>
            <Spinner color="information" />
          </EmptyStateGraphic>
          <EmptyStateTitle>Loading verification status</EmptyStateTitle>
          <EmptyStateDescription>Please wait while we check your verification status...</EmptyStateDescription>
        </EmptyState>
      </Box>
    );
  }

  if (error != null) {
    return (
      <Box p="400">
        <EmptyState>
          <EmptyStateTitle icon="error">Failed to load verification status</EmptyStateTitle>
          <EmptyStateDescription>Please refresh the page or try again later.</EmptyStateDescription>
        </EmptyState>
      </Box>
    );
  }

  if (status?.status === 'running') {
    return <VerificationStatusRunning />;
  }

  if (status?.status === 'failed') {
    return (
      <Box p="400">
        <EmptyState>
          <EmptyStateTitle icon="error">Verification failed</EmptyStateTitle>
          <EmptyStateDescription>
            {status.error ?? 'An error occurred during verification. Please try again.'}
          </EmptyStateDescription>
        </EmptyState>
      </Box>
    );
  }

  if (status?.status === 'completed' && status.result != null) {
    const { decision } = status.result;

    if (decision === 'approve') {
      return <VerificationStatusApproved workspaceId={workspaceId} />;
    }

    if (decision === 'deny' || decision === 'escalate') {
      return <VerificationStatusEscalated userEmail={auth.user?.email} />;
    }
  }

  return (
    <Box p="400">
      <EmptyState>
        <EmptyStateTitle icon="error">Unknown verification status</EmptyStateTitle>
        <EmptyStateDescription>Please contact support.</EmptyStateDescription>
      </EmptyState>
    </Box>
  );
}
